import {
  faCheck,
  faEye,
  faPaperPlane,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StarIcon from '@mui/icons-material/Star';
import { Chip } from '@mui/material';

import Avatar from '@/components/Avatar';
import Button from '@/components/Button';
import styled from '@/styles';
import { workerName } from '@/util/worker';

// Job-page-only fork of the tier badge. `dependabilityTier` is task B12's
// field on the backend search payload; it is `null` for every worker today
// because GRAVY-10231 (the job that actually populates it) has not merged
// yet, so the neutral "—" badge below is the normal case, not an edge case.
export type DependabilityTier = 'monitored' | 'standard' | 'gold' | 'platinum';

// Tier -> color, verbatim from the F12 brief.
const TIER_COLORS: Record<DependabilityTier, string> = {
  monitored: '#CF040C',
  standard: '#2169BA',
  gold: '#A28812',
  platinum: '#96A7BA',
};

const TIER_LABELS: Record<DependabilityTier, string> = {
  monitored: 'Monitored',
  standard: 'Standard',
  gold: 'Gold',
  platinum: 'Platinum',
};

export type WorkerRowV2Worker = {
  id: number | string;
  avatarUrl?: string | null;
  averageRating?: number | null;
  jobsRatingCount?: number | null;
  selectedSkillShift?: number | null;
  shiftsWith?: number | null;
  dependabilityTier?: DependabilityTier | null;
  hasConflicts?: boolean;
  conflictDates?: string[];
  // GRAVY-10395: the staffing readiness gate, per row. Only an EXPLICIT
  // 0/false means not-ready — older payloads without the field must not
  // flag anyone.
  readyToWork?: number | boolean;
  notReadyReason?: string | null;
  // Whether the backend considers this worker eligible for each action at
  // all (distinct from `disabled`, which gates an eligible action off
  // temporarily — e.g. no schedule selected, all-past selection, quality
  // gate). See StaffingWorkerProfileRow.tsx:128-141, which these two flags
  // port render-visibility from.
  hireable?: boolean;
  invitable?: boolean;
  user?: {
    firstName?: string;
    middleName?: string;
    lastName?: string;
  } | null;
};

export type WorkerRowV2Props = {
  worker: WorkerRowV2Worker;
  skillName: string;
  isHired?: boolean;
  isInvited?: boolean;
  onHire: (worker: WorkerRowV2Worker) => void;
  onInvite: (worker: WorkerRowV2Worker) => void;
  // Independently controllable — mirrors StaffingWorkerProfileRow's
  // buttonsDisableCheck (hire) vs inviteButtonDisable (invite). The
  // original never disables Hire when all selected schedules are past (a
  // deliberate affordance for backfilling a hire onto a completed job); it
  // does disable Invite in that case. A single shared `disabled` prop can't
  // express that asymmetry — see PreferredStaffingModal's usage.
  hireDisabled?: boolean;
  inviteDisabled?: boolean;
  // Whether each button RENDERS at all — ported verbatim from
  // StaffingWorkerProfileRow.tsx:128-141:
  //   Hire renders only when worker.hireable && (!isClientAdmin || isLTA)
  //   Invite renders only when worker.invitable && (!isLTA || hasConflicts)
  // Both default false, matching the original's isClientAdmin/isLTA props
  // (neither optional there — defaulted here only so existing non-fork
  // callers/tests that predate this prop don't need every call site
  // touched; PreferredStaffingModal always passes both explicitly).
  isClientAdmin?: boolean;
  isLTA?: boolean;
};

// Inline equivalent of StaffingUtils' buttonStyles. Duplicated on purpose —
// this fork may not import from the shared StaffingUtils file (see F12
// brief); the palette below is small and stable, so keeping a second copy in
// sync is a low-risk trade against coupling this fork to shared internals.
const buttonStyles = {
  add: { borderColor: '#45A735', color: '#2E8722' },
  addDisabled: { borderColor: '#DADADA', color: '#979797' },
  done: { borderColor: '#262626', background: '#262626' },
};

// Geometry of the prototype's .pool-action-btn (32h / 80w / radius 4 /
// 12px-600), overriding ButtonElement's 36h / 18px-padding / $lg radius
// default so a row of actions reads as compactly as the prototype's.
const actionButtonCss = {
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 600,
  gap: '4px',
  height: '32px',
  lineHeight: '1',
  minWidth: '80px',
  padding: '0 10px',
};

// Layout below is a direct port of the prototype's .modal-user-row block
// (preferred-worker-pool/index.html:1133-1187): a full-width row with the
// actions pinned to the right edge and a hairline between consecutive
// workers. The previous version nested Stacks, each defaulting to
// `width: 100%`, so the two halves split the row 50/50 and the buttons
// landed mid-row instead of flush right.
const Row = styled('div', {
  alignItems: 'center',
  borderBottom: '1px solid #EAEAEA',
  display: 'flex',
  gap: '40px',
  justifyContent: 'space-between',
  padding: '20px 12px 20px 0',
  width: '100%',
});

const Info = styled('div', {
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  gap: '20px',
  minWidth: 0,
});

const AvatarSlot = styled('div', {
  flexShrink: 0,
  height: '60px',
  width: '60px',
});

const Meta = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
});

const NameRow = styled('div', {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

const Name = styled('span', {
  color: '#1C1C1C',
  fontSize: '14px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
});

// GRAVY-10849: the line wraps instead of running past the Info column and
// under the pinned `Actions` buttons. GRAVY-10801's longer labels ("… shifts
// (platform-wide)", "shifts with this client") no longer fit this modal's
// narrower columns, and the prototype's plain `white-space: nowrap` has no
// answer for that. Wrapping is preferred over truncating because both counts
// drive the staffing decision, so neither may be hidden behind an ellipsis.
const Stats = styled('div', {
  alignItems: 'center',
  color: '#1C1C1C',
  display: 'flex',
  flexWrap: 'wrap',
  fontSize: '14px',
  gap: '8px',
  minWidth: 0,

  '& b': { fontWeight: 700 },
});

// One count travels with its own separator and label, so a wrap can never
// split "17" from "parking attendant shifts (platform-wide)" or orphan a dot
// at the end of a line.
const Stat = styled('span', {
  alignItems: 'center',
  display: 'inline-flex',
  gap: '8px',
  minWidth: 0,
  whiteSpace: 'nowrap',
});

// Last-resort clip for a skill name long enough to overflow even its own
// line — the row stays inside its column in every case.
const StatLabel = styled('span', {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// The prototype's .modal-user-stats .dot separator.
const Dot = styled('span', {
  backgroundColor: '#DADADA',
  borderRadius: '50%',
  flexShrink: 0,
  height: '4px',
  width: '4px',
});

// Rating has no prototype equivalent — it rides in the stats line rather
// than on a row of its own so the card keeps the prototype's two-line
// rhythm while retaining the signal the old staffing modal showed.
const Rating = styled('span', {
  alignItems: 'center',
  display: 'inline-flex',
  gap: '4px',
});

const RatingCount = styled('span', {
  color: '#979797',
});

const Actions = styled('div', {
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  gap: '16px',
});

// Solid pill ported from the prototype's .tier-badge (2026-07-30
// prototype-alignment batch): white text on the tier color, 12px/700,
// radius 8, with the tier icon (star; eye for monitored — the prototype's
// only non-star tier icon). The null state keeps the earlier neutral chip:
// the prototype has no null contract (every seeded row has a tier) and "—"
// stays the QA-visible normal case until GRAVY-10231 populates tiers.
const TierPill = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  borderRadius: 8,
  padding: '3px 8px',
  fontSize: '12px',
  fontWeight: 700,
  color: '#fff',
  whiteSpace: 'nowrap',
});

const TierBadge = ({ tier }: { tier?: DependabilityTier | null }) => {
  if (!tier) {
    return (
      <Chip
        data-testid="tier-badge"
        label="—"
        size="small"
        sx={{
          backgroundColor: '#F3F4F6',
          color: '#6B7280',
          fontSize: '11px',
          height: '20px',
          fontWeight: 600,
        }}
      />
    );
  }
  return (
    <TierPill
      data-testid="tier-badge"
      style={{ background: TIER_COLORS[tier] }}
    >
      <FontAwesomeIcon
        icon={tier === 'monitored' ? faEye : faStar}
        style={{ width: 12, height: 12 }}
      />
      {TIER_LABELS[tier]}
    </TierPill>
  );
};

const WorkerRowV2 = ({
  worker,
  skillName,
  isHired = false,
  isInvited = false,
  onHire,
  onInvite,
  hireDisabled = false,
  inviteDisabled = false,
  isClientAdmin = false,
  isLTA = false,
}: WorkerRowV2Props) => {
  const isRookie = worker.shiftsWith === 0;
  const hasRating =
    worker.averageRating != null && Number(worker.averageRating) > 0;
  // GRAVY-10395 (Product decision 59948 §B, option b): a not-staffing-ready
  // pool member is shown flagged unavailable-with-reason — the same amber
  // treatment as the busy-worker conflict chip below. The backend already
  // withholds hireable/invitable for these rows, so no buttons render.
  const notReady = worker.readyToWork === 0 || worker.readyToWork === false;

  // Ported verbatim from StaffingWorkerProfileRow.tsx:128-141 — see
  // WorkerRowV2Props for the source-of-truth comment.
  const showHire = !!worker.hireable && (!isClientAdmin || isLTA);
  const showInvite = !!worker.invitable && (!isLTA || !!worker.hasConflicts);
  // Mirrors getInvitedButton's `disable: isHiredButton || inviteButtonDisable`
  // — an already-hired worker's Invite button (when it renders at all) must
  // stay disabled, a term PreferredStaffingModal's shared inviteRowDisabled
  // never carried.
  const effectiveInviteDisabled = inviteDisabled || isHired;

  return (
    <Row data-testid="worker-row">
      <Info>
        <AvatarSlot>
          <Avatar
            rounded
            css={{ boxSize: 60 }}
            firstName={worker.user?.firstName}
            lastName={worker.user?.lastName}
            src={worker.avatarUrl ?? undefined}
          />
        </AvatarSlot>
        <Meta>
          <NameRow>
            <Name>{workerName(worker)}</Name>
            <TierBadge tier={worker.dependabilityTier} />
            {isRookie && (
              <Chip
                label="Rookie"
                size="small"
                sx={{
                  backgroundColor: '#EEF2FF',
                  color: '#4338CA',
                  fontSize: '11px',
                  height: '20px',
                  fontWeight: 600,
                }}
              />
            )}
            {notReady && (
              <Chip
                label={
                  worker.notReadyReason
                    ? `Unavailable — ${worker.notReadyReason}`
                    : 'Unavailable'
                }
                size="small"
                sx={{
                  backgroundColor: '#FEF3C7',
                  color: '#92400E',
                  fontSize: '11px',
                  height: '20px',
                  fontWeight: 500,
                }}
              />
            )}
            {!notReady && !!worker.hasConflicts && (
              <Chip
                label={
                  worker.conflictDates?.length
                    ? `Unavailable ${worker.conflictDates.join(', ')}`
                    : 'Unavailable'
                }
                size="small"
                sx={{
                  backgroundColor: '#FEF3C7',
                  color: '#92400E',
                  fontSize: '11px',
                  height: '20px',
                  fontWeight: 500,
                }}
              />
            )}
          </NameRow>
          {/* Count wording per the prototype's .modal-user-stats rows
              ("64 bartender shifts · 12 shifts with client") — deliberately
              diverges from the old modal's "{Skill} Shifts | Shifts With
              You" (handoff §8.3 delta B). GRAVY-10801 labels the skill count
              "(platform-wide)": selectedSkillShift counts every assignment
              in the skill across all clients, while pool rows show approved
              shifts only, so the same worker can legitimately read 17 here
              and 0 on the pool card. Coerce through Number(): the search
              payload sends '' (not null) for a worker with no shifts in the
              selected skill, so `?? 0` would not catch it and the prototype's
              leading number rendered as an empty <b>. */}
          <Stats data-testid="worker-row-stats">
            <Rating>
              <StarIcon
                sx={{
                  color: hasRating ? '#faaf00' : '#D1D5DB',
                  height: '16px',
                  width: '16px',
                }}
              />
              {hasRating ? (
                <>
                  <b>{Number(worker.averageRating).toFixed(1)}</b>
                  {worker.jobsRatingCount != null &&
                    Number(worker.jobsRatingCount) > 0 && (
                      <RatingCount>{`(${worker.jobsRatingCount})`}</RatingCount>
                    )}
                </>
              ) : (
                <RatingCount>—</RatingCount>
              )}
            </Rating>
            <Stat>
              <Dot />
              <b>{Number(worker.selectedSkillShift) || 0}</b>
              <StatLabel>
                {`${(skillName || '').toLowerCase()} shifts (platform-wide)`}
              </StatLabel>
            </Stat>
            <Stat>
              <Dot />
              <b>{Number(worker.shiftsWith) || 0}</b>
              <StatLabel>shifts with this client</StatLabel>
            </Stat>
          </Stats>
        </Meta>
      </Info>
      <Actions data-testid="worker-row-actions">
        {showHire &&
          (isHired ? (
            // Terminal state, not interactive: the F11 hook has no `mode`
            // concept, so this row never offers a "remove" action.
            <Button
              a11yLabel="Hired"
              css={actionButtonCss}
              disabled
              iconLeft={faCheck}
              label="Hired"
              style={buttonStyles.done}
              type="button"
              onClick={() => undefined}
            />
          ) : (
            <Button
              a11yLabel="Hire"
              appearance="outline"
              css={actionButtonCss}
              disabled={hireDisabled}
              iconLeft={faCheck}
              label="Hire"
              style={hireDisabled ? buttonStyles.addDisabled : buttonStyles.add}
              type="button"
              onClick={() => onHire(worker)}
            />
          ))}
        {showInvite &&
          (isInvited ? (
            // Terminal state, not interactive — see the Hired button above.
            <Button
              a11yLabel="Invited"
              css={actionButtonCss}
              disabled
              iconLeft={faCheck}
              label="Invited"
              style={buttonStyles.done}
              type="button"
              onClick={() => undefined}
            />
          ) : (
            <Button
              a11yLabel="Invite"
              appearance="outline"
              css={actionButtonCss}
              disabled={effectiveInviteDisabled}
              iconLeft={faPaperPlane}
              label="Invite"
              style={
                effectiveInviteDisabled
                  ? buttonStyles.addDisabled
                  : buttonStyles.add
              }
              type="button"
              onClick={() => onInvite(worker)}
            />
          ))}
      </Actions>
    </Row>
  );
};

export default WorkerRowV2;
